import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { useThemePalette } from '@/config/theme';
import type { PlayerLevel } from '@/lib/data/provider';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

/** A compact "Lv N" chip + XP progress bar toward the next level. */
export function LevelBar({ level }: { level: PlayerLevel }) {
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const palette = useThemePalette();
  const pct = level.xp_for_next > 0 ? Math.min(100, Math.round((level.xp_into_level / level.xp_for_next) * 100)) : 0;
  const n = (v: number) => formatNumber(v, numeralOptions);

  return (
    <View className="gap-1.5">
      <View className="flex-row items-center justify-between">
        <View
          className="flex-row items-center gap-1 rounded-full px-2.5 py-0.5"
          style={{ backgroundColor: palette.voltGlass }}
        >
          <Ionicons name="ribbon" size={12} color={palette.voltBright} />
          <AppText variant="micro" weight="bold" style={{ color: palette.voltBright }}>
            {t('rewards.level.chip', { level: n(level.level) })}
          </AppText>
        </View>
        <AppText variant="micro" tabular className="text-muted">
          {t('rewards.level.xp', { into: n(level.xp_into_level), total: n(level.xp_for_next) })}
        </AppText>
      </View>
      <View className="h-2 overflow-hidden rounded-full" style={{ backgroundColor: palette.inset }}>
        <View style={{ width: `${pct}%`, height: '100%', backgroundColor: palette.volt }} />
      </View>
    </View>
  );
}
